/**
 * Messages - every conversation you are part of
 * ----------------------------------------------
 * One per agreed job, most recently active first. There is no way to start a
 * conversation from this screen, and that is deliberate: a thread exists only
 * because a student accepted a chore, so the list is a consequence of work
 * agreed rather than something anybody can add to.
 *
 * Drawn as one list with hairlines between the rows, not as a stack of
 * separate cards. On a phone a card each meant four borders, a shadow and a
 * gap per conversation, which is a lot of furniture around two lines of text -
 * and it stopped the eye running down the column the way a list does.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Lock, ChevronRight, LifeBuoy, Archive, ArchiveRestore } from 'lucide-react';

import { messageApi } from '../services/api';
import { dateTime, timeAgo } from '../utils/format';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import Avatar from '../components/Avatar';

import { useI18n } from '../hooks/useI18n';

export default function Messages() {
  const { t } = useI18n();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /**
   * The filed-away ones are a separate view rather than a greyed-out row in
   * the same list: the whole point of archiving is that they are not in the
   * way. Nothing is deleted and the other person sees no change - see
   * backend/models/ThreadState.js.
   */
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    messageApi
      .threads(showArchived)
      .then((d) => { if (!cancelled) setThreads(d.threads || []); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [showArchived]);

  /** Puts a conversation back where it was. */
  const restore = async (thread) => {
    try {
      if (thread.kind === 'offer') {
        await messageApi.offerUnarchive(thread.offer._id, thread.withPoster || undefined);
      } else {
        await messageApi.unarchive(thread.chore._id, thread.withStudent || undefined);
      }
      setThreads((list) => list.filter((x) => x !== thread));
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Loader label={t('common.loadingMessages')} />;

  return (
    <div className="container section">
      <div className="page-header">
        <h1>{t('messages.title')}</h1>
        <p>
          {t('messages.subtitle')}
        </p>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>{error}</div>}

      <div className="row" style={{ marginBottom: 'var(--space-md)' }}>
        <button
          type="button"
          className="btn btn-outline btn-sm btn-inline"
          onClick={() => setShowArchived((v) => !v)}
        >
          {showArchived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
          {showArchived ? t('messages.backToInbox') : t('messages.seeArchived')}
        </button>
      </div>

      {/* ---- Reporting that the site is broken ----
          Pinned above the job conversations and always there, even when there
          are none - which is exactly when somebody is most likely to think
          the site is broken and need a way to say so.

          It is a chat, so it lives with the chats. Adding it to the navbar
          instead would have made a seventh item to reach a screen most people
          will use once. */}
      <Link to="/report-issue" className="thread" style={{ marginBottom: 'var(--space-md)' }}>
        <span className="feature-icon" aria-hidden="true">
          <LifeBuoy size={20} />
        </span>
        <div className="thread-body">
          <div className="thread-top">
            <strong className="thread-name">{t('reportIssue.title')}</strong>
          </div>
          <div className="thread-preview">{t('reportIssue.listHint')}</div>
        </div>
        <ChevronRight size={18} className="thread-chevron" aria-hidden="true" />
      </Link>

      {threads.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title={t('messages.emptyTitle')}
          message={t('messages.emptyBody')}
          action={<Link to="/marketplace" className="btn btn-primary btn-inline">{t('nav.browse')}</Link>}
        />
      ) : (
        <div className="thread-list">
          {/* `thread`, not `t` - `t` is the translator. Naming the loop
              variable `t` shadowed it and turned t('messages.you') into a
              call on the thread object. */}
          {threads.map((thread) => (
            <Link
              to={thread.kind === 'offer'
                ? `/messages/offer/${thread.offer._id}${thread.withPoster ? `?with=${thread.withPoster}` : ''}`
                : `/messages/${thread.chore._id}${thread.withStudent ? `?with=${thread.withStudent}` : ''}`}
              key={thread.kind === 'offer'
                ? `offer:${thread.offer._id}:${thread.withPoster || 'me'}`
                : `${thread.chore._id}:${thread.withStudent || 'me'}`}
              className={`thread${thread.unread > 0 ? ' is-unread' : ''}`}
            >
              <Avatar person={thread.other} />

              <div className="thread-body">
                <div className="thread-top">
                  <strong className="thread-name">
                    {thread.other.firstName} {thread.other.lastName?.[0]}.
                  </strong>
                  <span className="thread-when">
                    {thread.lastMessage
                      ? timeAgo(thread.lastMessage.createdAt)
                      : dateTime(thread.chore?.scheduledFor)}
                  </span>
                </div>

                <div className="thread-about">
                  {!thread.open && <Lock size={13} aria-hidden="true" />}
                  {thread.kind === 'offer' ? t('messages.aboutTheirTimes') : thread.chore.title}
                  {thread.enquiry && <> &middot; {t('messages.aQuestion')}</>}
                </div>

                {thread.lastMessage && (
                  <div className="thread-preview">
                    {thread.lastMessage.mine && <span className="text-muted">{t('messages.you')} </span>}
                    {thread.lastMessage.body}
                  </div>
                )}
              </div>

              {/* The count sits where an unread count sits in every messaging
                  app - on the right, on the row, not as a badge by the name. */}
              {/* eslint-disable-next-line no-nested-ternary */}
              {showArchived ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm btn-inline"
                  onClick={(e) => { e.preventDefault(); restore(thread); }}
                >
                  {t('messages.putBack')}
                </button>
              ) : thread.unread > 0 ? (
                <span className="thread-unread" aria-label={`${thread.unread} unread`}>
                  {thread.unread}
                </span>
              ) : (
                <ChevronRight size={18} className="thread-chevron" aria-hidden="true" />
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
